import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { MapsState, IMap, MapResponse } from '../types';
import { ApiService } from '../services/api';
import { RootState } from './index';

// Helper function to create ApiService with current token
const createApiService = (getState: () => RootState) => {
    const state = getState();
    const apiService = new ApiService(
        process.env.REACT_APP_API_URL || 'http://localhost:3051',
        process.env.REACT_APP_API_URL || 'http://localhost:3051/api'
    );
    // Use token from Redux state, or fallback to localStorage if state hasn't been hydrated yet
    const token = state.auth.token || localStorage.getItem('token');
    if (token) {
        apiService.setToken(token);
    }
    return apiService;
};

const initialState: MapsState = {
    entities: {},
    currentMapId: null,
    userMaps: [],
    sharedMaps: [],
    loading: false,
    loaded: false, // Track if initial load completed
    viewMode: 'tabs',
    mapViews: {},
    error: null,
};

// Async thunks
export const fetchMaps = createAsyncThunk(
    'maps/fetchMaps',
    async (_, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.fetchMaps();
    }
);

export const fetchSharedMaps = createAsyncThunk(
    'maps/fetchSharedMaps',
    async (_, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.fetchSharedMaps();
    }
);

export const fetchPublicMaps = createAsyncThunk(
    'maps/fetchPublicMaps',
    async (params: { limit?: number; skip?: number } = {}, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.fetchPublicMaps(params.limit, params.skip);
    }
);

export const fetchMap = createAsyncThunk(
    'maps/fetchMap',
    async (mapId: string, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.fetchMap(mapId);
    }
);

export const createMap = createAsyncThunk(
    'maps/createMap',
    async (mapData: {
        name: string;
        description?: string;
        backgroundImage?: string;
        settings?: any;
        metadata?: any;
        isPublic?: boolean;
    }, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.createMap(mapData);
    }
);

export const updateMap = createAsyncThunk(
    'maps/updateMap',
    async (params: {
        mapId: string;
        updates: {
            name?: string;
            description?: string;
            backgroundImage?: string;
            settings?: any;
            metadata?: any;
            isPublic?: boolean;
        };
    }, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.updateMap(params.mapId, params.updates);
    }
);

export const deleteMap = createAsyncThunk(
    'maps/deleteMap',
    async (mapId: string, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        const result = await apiService.deleteMap(mapId);
        return { ...result, mapId };
    }
);

export const duplicateMap = createAsyncThunk(
    'maps/duplicateMap',
    async (mapId: string, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.duplicateMap(mapId);
    }
);

// Collaboration async thunks
export const addMapCollaborator = createAsyncThunk(
    'maps/addMapCollaborator',
    async (params: {
        mapId: string;
        userId: string;
        role: 'editor' | 'viewer';
    }, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        const result = await apiService.addMapCollaborator(params.mapId, params.userId, params.role);
        return { ...result, mapId: params.mapId };
    }
);

export const updateMapCollaborator = createAsyncThunk(
    'maps/updateMapCollaborator',
    async (params: {
        mapId: string;
        userId: string;
        role: 'editor' | 'viewer';
    }, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        const result = await apiService.updateMapCollaborator(params.mapId, params.userId, params.role);
        return { ...result, mapId: params.mapId };
    }
);

export const removeMapCollaborator = createAsyncThunk(
    'maps/removeMapCollaborator',
    async (params: {
        mapId: string;
        userId: string;
    }, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        const result = await apiService.removeMapCollaborator(params.mapId, params.userId);
        return { ...result, mapId: params.mapId };
    }
);

export const saveMapPreset = createAsyncThunk(
    'maps/saveMapPreset',
    async (params: {
        mapId: string;
        center: [number, number];
        zoom: number;
    }, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        const result = await apiService.saveMapPreset(params.mapId, params.center, params.zoom);
        return { ...result, mapId: params.mapId };
    }
);

const mapsSlice = createSlice({
    name: 'maps',
    initialState,
    reducers: {
        setCurrentMap: (state, action: PayloadAction<string | null>) => {
            state.currentMapId = action.payload;
            // Save to localStorage whenever map changes
            if (action.payload) {
                localStorage.setItem('lastViewedMap', action.payload);
            } else {
                localStorage.removeItem('lastViewedMap');
            }
        },

        setViewMode: (state, action: PayloadAction<'tabs' | 'grid'>) => {
            state.viewMode = action.payload;
        },

        clearMapsError: (state) => {
            state.error = null;
        },

        addLocalMap: (state, action: PayloadAction<IMap>) => {
            const map = action.payload;
            state.entities[map._id!] = map;
            if (!state.userMaps.includes(map._id!)) {
                state.userMaps.push(map._id!);
            }
        },

        updateLocalMap: (state, action: PayloadAction<{ mapId: string; updates: Partial<IMap> }>) => {
            const { mapId, updates } = action.payload;
            if (state.entities[mapId]) {
                state.entities[mapId] = { ...state.entities[mapId], ...updates };
            }
        },

        removeLocalMap: (state, action: PayloadAction<string>) => {
            const mapId = action.payload;
            delete state.entities[mapId];
            state.userMaps = state.userMaps.filter(id => id !== mapId);
            state.sharedMaps = state.sharedMaps.filter(id => id !== mapId);
            if (state.currentMapId === mapId) {
                state.currentMapId = null;
                localStorage.removeItem('lastViewedMap');
            }

            // Clean up map view from localStorage
            const viewKey = `mapView_${mapId}`;
            localStorage.removeItem(viewKey);
            delete state.mapViews[mapId];
        },

        resetMapsState: (state) => {
            Object.assign(state, initialState);
        },

        setMapView: (state, action: PayloadAction<{ mapId: string; center: [number, number]; zoom: number }>) => {
            const { mapId, center, zoom } = action.payload;

            // Check if values have actually changed to prevent infinite loops
            const existingView = state.mapViews[mapId];
            if (existingView) {
                const centerUnchanged =
                    Math.abs(existingView.center[0] - center[0]) < 0.01 &&
                    Math.abs(existingView.center[1] - center[1]) < 0.01;
                const zoomUnchanged = Math.abs(existingView.zoom - zoom) < 0.001;

                if (centerUnchanged && zoomUnchanged) {
                    return; // No actual change, skip state update
                }
            }

            state.mapViews[mapId] = { center, zoom };

            // Save view position to localStorage
            const viewKey = `mapView_${mapId}`;
            localStorage.setItem(viewKey, JSON.stringify({ center, zoom }));
        },

        clearMapView: (state, action: PayloadAction<string>) => {
            const mapId = action.payload;
            delete state.mapViews[mapId];

            // Also remove from localStorage
            const viewKey = `mapView_${mapId}`;
            localStorage.removeItem(viewKey);
        },

        restoreLastViewedMap: (state) => {
            const lastViewedMapId = localStorage.getItem('lastViewedMap');
            let mapIdToRestore = null;

            if (lastViewedMapId && state.entities[lastViewedMapId]) {
                mapIdToRestore = lastViewedMapId;
            } else {
                // Fallback to first available map
                const availableMaps = [...state.userMaps, ...state.sharedMaps];
                if (availableMaps.length > 0) {
                    mapIdToRestore = availableMaps[0];
                    localStorage.setItem('lastViewedMap', mapIdToRestore);
                }
            }

            if (mapIdToRestore) {
                state.currentMapId = mapIdToRestore;

                // Restore view position from localStorage if available
                const viewKey = `mapView_${mapIdToRestore}`;
                const savedView = localStorage.getItem(viewKey);
                if (savedView) {
                    try {
                        const viewData = JSON.parse(savedView);
                        if (viewData.center && viewData.zoom) {
                            state.mapViews[mapIdToRestore] = {
                                center: viewData.center,
                                zoom: viewData.zoom
                            };
                        }
                    } catch (error) {
                        // console.warn('Failed to parse saved view data for map', mapIdToRestore, error);
                    }
                }
            }
        }
    },
    extraReducers: (builder) => {
        // fetchMaps
        builder
            .addCase(fetchMaps.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMaps.fulfilled, (state, action) => {
                state.loading = false;
                state.loaded = true; // Mark as loaded
                if (action.payload.success && action.payload.maps) {
                    action.payload.maps.forEach(map => {
                        state.entities[map._id!] = map;
                        if (!state.userMaps.includes(map._id!)) {
                            state.userMaps.push(map._id!);
                        }
                    });
                }
            })
            .addCase(fetchMaps.rejected, (state, action) => {
                state.loading = false;
                state.loaded = true; // Mark as loaded even on error to prevent infinite retries
                state.error = action.error.message || 'Failed to fetch maps';
            });

        // fetchSharedMaps
        builder
            .addCase(fetchSharedMaps.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSharedMaps.fulfilled, (state, action) => {
                state.loading = false;
                state.loaded = true; // Mark as loaded
                if (action.payload.success && action.payload.maps) {
                    action.payload.maps.forEach(map => {
                        state.entities[map._id!] = map;
                        if (!state.sharedMaps.includes(map._id!)) {
                            state.sharedMaps.push(map._id!);
                        }
                    });
                }
            })
            .addCase(fetchSharedMaps.rejected, (state, action) => {
                state.loading = false;
                state.loaded = true; // Mark as loaded even on error to prevent infinite retries
                state.error = action.error.message || 'Failed to fetch shared maps';
            });

        // fetchPublicMaps
        builder
            .addCase(fetchPublicMaps.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPublicMaps.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload.success && action.payload.maps) {
                    action.payload.maps.forEach(map => {
                        state.entities[map._id!] = map;
                    });
                }
            })
            .addCase(fetchPublicMaps.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch public maps';
            });

        // fetchMap
        builder
            .addCase(fetchMap.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMap.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload.success && action.payload.map) {
                    const map = action.payload.map;
                    state.entities[map._id!] = map;
                }
            })
            .addCase(fetchMap.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch map';
            });

        // createMap
        builder
            .addCase(createMap.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createMap.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload.success && action.payload.map) {
                    const map = action.payload.map;
                    state.entities[map._id!] = map;
                    state.userMaps.push(map._id!);
                    state.currentMapId = map._id!;
                }
            })
            .addCase(createMap.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to create map';
            });

        // updateMap
        builder
            .addCase(updateMap.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateMap.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload.success && action.payload.map) {
                    const map = action.payload.map;
                    state.entities[map._id!] = map;
                }
            })
            .addCase(updateMap.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to update map';
            });

        // deleteMap
        builder
            .addCase(deleteMap.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteMap.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload.success) {
                    const mapId = action.payload.mapId;
                    delete state.entities[mapId];
                    state.userMaps = state.userMaps.filter(id => id !== mapId);
                    state.sharedMaps = state.sharedMaps.filter(id => id !== mapId);

                    // Clean up localStorage for deleted map
                    const viewKey = `mapView_${mapId}`;
                    localStorage.removeItem(viewKey);
                    delete state.mapViews[mapId];

                    if (state.currentMapId === mapId) {
                        // Switch to the first available map
                        const availableMaps = [...state.userMaps, ...state.sharedMaps];
                        const newMapId = availableMaps.length > 0 ? availableMaps[0] : null;
                        state.currentMapId = newMapId;

                        // Update localStorage with new current map
                        if (newMapId) {
                            localStorage.setItem('lastViewedMap', newMapId);
                        } else {
                            localStorage.removeItem('lastViewedMap');
                        }
                    }
                }
            })
            .addCase(deleteMap.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to delete map';
            });

        // duplicateMap
        builder
            .addCase(duplicateMap.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(duplicateMap.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload.success && action.payload.map) {
                    const map = action.payload.map;
                    state.entities[map._id!] = map;
                    state.userMaps.push(map._id!);
                }
            })
            .addCase(duplicateMap.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to duplicate map';
            });

        // Collaboration reducers
        builder
            .addCase(addMapCollaborator.fulfilled, (state, action) => {
                // Handle successful collaboration update
                if (action.payload.success) {
                    // You might want to refetch the map or update local state
                }
            })
            .addCase(updateMapCollaborator.fulfilled, (state, action) => {
                // Handle successful collaboration update
                if (action.payload.success) {
                    // You might want to refetch the map or update local state
                }
            })
            .addCase(removeMapCollaborator.fulfilled, (state, action) => {
                // Handle successful collaboration removal
                if (action.payload.success) {
                    // You might want to refetch the map or update local state
                }
            });

        // Save map preset reducer
        builder.addCase(saveMapPreset.fulfilled, (state, action) => {
            if (action.payload.success && action.payload.savedView) {
                const mapId = action.payload.mapId;
                if (state.entities[mapId]) {
                    // Update the map entity with the new savedView
                    state.entities[mapId].settings.savedView = action.payload.savedView;
                }
            }
        });
    }
});

export const {
    setCurrentMap,
    setViewMode,
    clearMapsError,
    addLocalMap,
    updateLocalMap,
    removeLocalMap,
    resetMapsState,
    setMapView,
    clearMapView,
    restoreLastViewedMap
} = mapsSlice.actions;

// Selectors
const selectMapsState = (state: RootState) => state.maps;
const selectMapEntities = createSelector(selectMapsState, (mapsState) => mapsState.entities);
const selectUserMapIds = createSelector(selectMapsState, (mapsState) => mapsState.userMaps);
const selectSharedMapIds = createSelector(selectMapsState, (mapsState) => mapsState.sharedMaps);

export const selectCurrentMap = createSelector(selectMapsState, (mapsState): IMap | null => {
    return mapsState.currentMapId ? mapsState.entities[mapsState.currentMapId] || null : null;
});

export const selectUserMaps = createSelector(
    [selectMapEntities, selectUserMapIds],
    (entities, ids): IMap[] => ids.map((id: string) => entities[id]).filter(Boolean)
);

export const selectSharedMaps = createSelector(
    [selectMapEntities, selectSharedMapIds],
    (entities, ids): IMap[] => ids.map((id: string) => entities[id]).filter(Boolean)
);

export const selectAllUserMaps = createSelector(
    [selectUserMaps, selectSharedMaps],
    (userMaps, sharedMaps): IMap[] => [...userMaps, ...sharedMaps]
);

export const selectMapById = (state: RootState, mapId: string): IMap | null => {
    return state.maps.entities[mapId] || null;
};

export const selectMapsLoading = (state: RootState): boolean => {
    return state.maps.loading;
};

export const selectMapsError = (state: RootState): string | null => {
    return state.maps.error || null;
};

export const selectViewMode = (state: RootState): 'tabs' | 'grid' => {
    return state.maps.viewMode;
};

export const selectCurrentMapId = (state: RootState): string | null => {
    return state.maps.currentMapId;
};

export const selectMapView = createSelector(
    [selectMapsState, (_state: RootState, mapId: string) => mapId],
    (mapsState, mapId): { center: [number, number]; zoom: number } | null => mapsState.mapViews[mapId] || null
);

export const selectCurrentMapView = createSelector(
    [selectMapsState],
    (mapsState): { center: [number, number]; zoom: number } | null =>
        mapsState.currentMapId ? mapsState.mapViews[mapsState.currentMapId] || null : null
);

export const selectMapsWithStats = createSelector(
    [selectAllUserMaps, selectMapsState],
    (allMaps, mapsState) => ({
        maps: allMaps,
        totalMaps: allMaps.length,
        userOwnedMaps: mapsState.userMaps.length,
        sharedMaps: mapsState.sharedMaps.length,
        currentMapId: mapsState.currentMapId,
        viewMode: mapsState.viewMode,
        loading: mapsState.loading,
        error: mapsState.error
    })
);

export default mapsSlice.reducer;
